import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Fragment, useEffect, useState } from 'react';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import type { StorySlide } from '@sorare/core/src/components/Story';
import { Story } from '@sorare/core/src/components/Story';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';

import { ChooseYourRewardStory } from '#src/components/missions/DailyTask/DecisivePlayerPickerTaskStatus/ChooseYourRewardStory/index.tsx';
import { RewardUnlockedStory } from '#src/components/missions/stories/RewardUnlockedStory/index.tsx';
import { useClaimTask } from '#src/components/missions/useClaimTask.tsx';

import { isType } from '@sorare/core/src/lib/gql/index.ts';
import type { AnyTaskBasicClaimStory_taskInterface } from './__generated__/index.graphql.ts';

type Props = {
  task: AnyTaskBasicClaimStory_taskInterface;
  onClose: () => void;
  showContinueButton?: boolean;
};
export const AnyTaskBasicClaimStory = ({
  task,
  onClose,
  showContinueButton = true,
}: Props) => {
  const [
    claimTask,
    { loading: loadingClaimMutation, called: calledClaimMutation },
  ] = useClaimTask();
  const { sport } = useSportContext();
  const { isMobileApp, postMessage } = useIsMobileApp();
  const rewardConfigs = task.rewardConfigs || [];
  const alternativeRewardConfigs = task.alternativeRewardConfigs || [];
  const canChooseAlternativeRewardConfigs = alternativeRewardConfigs.length > 0;
  const [rewardPictureUrl, setRewardPictureUrl] = useState<string | null>(null);

  const [signedAlternativeRewardConfigs, setSignedAlternativeRewardConfigs] =
    useState<string | undefined>();

  useEffect(() => {
    const doClaimTask = async () => {
      const { data } = await claimTask({
        variables: {
          taskId: task.id,
          sport,
        },
      });
      const reward = data?.claimTask?.rewards?.at(0);
      if (isType(reward, 'AnyCardReward')) {
        setRewardPictureUrl(reward.card.pictureUrl);
      }
    };
    if (
      !canChooseAlternativeRewardConfigs &&
      task.aasmState === ManagerProgressionTaskState.COMPLETED
    ) {
      doClaimTask();
    }
  }, [
    canChooseAlternativeRewardConfigs,
    claimTask,
    task.id,
    task.aasmState,
    sport,
  ]);

  if (
    !canChooseAlternativeRewardConfigs &&
    (loadingClaimMutation || !calledClaimMutation)
  ) {
    return null;
  }

  return (
    <Story
      goBackToBeginning={false}
      onClose={() => {
        if (isMobileApp) {
          postMessage('taskClaimed', { taskId: task.id });
        }
        onClose();
      }}
      stories={[
        ...(canChooseAlternativeRewardConfigs
          ? [
              {
                duration: 2,
                withBackChevron: false,
                withNextChevron: false,
                autoPlayNext: false,
                bodyElement: ({ onNext }) => (
                  <Fragment key="choose-reward-story">
                    <ChooseYourRewardStory
                      rewardConfigs={rewardConfigs}
                      alternativeRewardConfigs={alternativeRewardConfigs}
                      setSignedAlternativeRewardConfigs={
                        setSignedAlternativeRewardConfigs
                      }
                      onConfirm={async () => {
                        await claimTask({
                          variables: {
                            taskId: task.id,
                            signedAlternativeRewardConfigs,
                            sport,
                          },
                        });
                        onNext();
                      }}
                    />
                  </Fragment>
                ),
              } as StorySlide,
            ]
          : []),
        {
          duration: 2,
          withBackChevron: false,
          withNextChevron: false,
          autoPlayNext: false,
          bodyElement: ({ onNext }) => (
            <Fragment key="reward-unlocked-story">
              <RewardUnlockedStory
                onClose={onNext}
                task={task}
                alternativeRewardConfigs={
                  signedAlternativeRewardConfigs
                    ? alternativeRewardConfigs[0].rewardConfigs
                    : undefined
                }
                showContinueButton={showContinueButton}
                rewardPictureUrl={rewardPictureUrl}
              />
            </Fragment>
          ),
        },
      ]}
    />
  );
};

AnyTaskBasicClaimStory.fragments = {
  taskInterface: gql`
    fragment AnyTaskBasicClaimStory_taskInterface on TaskInterface {
      id
      aasmState
      rewardConfigs {
        id
        ...ChooseYourRewardStory_rewardConfig
      }
      alternativeRewardConfigs {
        ...ChooseYourRewardStory_alternativeRewardConfig
      }
      ...RewardUnlockedStory_taskInterface
    }
    ${ChooseYourRewardStory.fragments.rewardConfig}
    ${ChooseYourRewardStory.fragments.alternativeRewardConfig}
    ${RewardUnlockedStory.fragments.taskInterface}
  ` as TypedDocumentNode<AnyTaskBasicClaimStory_taskInterface>,
};
